import { useState, type ComponentProps } from "react";
import { ArrowRight, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBooking } from "@/lib/booking";
import { openLeadFormDialog } from "@/components/LeadFormDialog";
import {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import recentLaunchesBg from "@/assets/recent-launches-bg.jpg.asset.json";
import flexwithfeMockupMain from "@/assets/flexwithfe-mockup-main.png.asset.json";
import flexwithfeMockupHome from "@/assets/flexwithfe-mockup-home-flat.jpg";
import flexwithfeMockupPages from "@/assets/flexwithfe-mockup-pages-flat.jpg";
import northstarMockupMain from "@/assets/northstar-mockup-main-new.png.asset.json";
import northstarMockupHome from "@/assets/northstar-mockup-home-flat.jpg";
import northstarMockupPages from "@/assets/northstar-mockup-pages.png.asset.json";
import lovingmefirstMockupDevices from "@/assets/lovingmefirst-mockup-devices.png.asset.json";
import lovingmefirstMockupLaptop from "@/assets/lovingmefirst-mockup-laptop.png.asset.json";
import lovingmefirstMockupAdditional from "@/assets/lovingmefirst-mockup-additional.png.asset.json";
import lovingmefirstMockupFull from "@/assets/lovingmefirst-mockup-full-new.jpg.asset.json";
import griefgardenerMockupMain from "@/assets/griefgardener-mockup-main.png.asset.json";
import griefgardenerMockupSecond from "@/assets/griefgardener-mockup-second.png.asset.json";
import griefgardenerMockupDevices from "@/assets/griefgardener-mockup-devices.png.asset.json";
import highermarkMockupMain from "@/assets/highermark-mockup-main.png.asset.json";
import highermarkMockupSecond from "@/assets/highermark-mockup-second.png.asset.json";
import highermarkMockupThird from "@/assets/highermark-mockup-third.png.asset.json";
import highermarkMockupFourth from "@/assets/highermark-mockup-fourth.png.asset.json";
import mavenyxMockupLaptop from "@/assets/mavenyx-mockup-laptop-optimized.jpg.asset.json";
import mavenyxMockupFull from "@/assets/mavenyx-mockup-full-optimized.jpg.asset.json";
import mavenyxMockupMonitor from "@/assets/mavenyx-mockup-monitor-optimized.jpg.asset.json";
import chicagoseniorridesMockup1 from "@/assets/chicagoseniorrides-mockup-1.png.asset.json";
import chicagoseniorridesMockup2 from "@/assets/chicagoseniorrides-mockup-2.png.asset.json";
import chicagoseniorridesMockup3 from "@/assets/chicagoseniorrides-mockup-3.png.asset.json";

function BookButton({ children, useLeadForm, ...props }: ComponentProps<typeof Button> & { useLeadForm?: boolean }) {
  const { openBooking } = useBooking();
  return (
    <Button onClick={useLeadForm ? openLeadFormDialog : openBooking} {...props}>
      {children}
    </Button>
  );
}

const recentLaunches = [
  {
    name: "Felecia Lamison",
    cardHeading: "From weeks of DIY to launch-ready in three days.",
    popupHeading: "From weeks of DIY to launch-ready in three days.",
    url: "https://flexwithfe.com/",
    website: "FlexWithFE · Executive Coaching & Consulting",
    quote: "The interactive pop-up features like the scorecard and assessment on the home page and throughout the site.",
    story: "Working with Three Day Launch was a wonderful experience, starting day one, from the first interaction to the completion of the site. If you are looking for a professional and reliable vendor who will guide you on best practices and how to narrow your focus, I highly recommend Three Day Launch.",
    caseStudy: "Felecia had a book on the horizon, new opportunities ahead, and a DIY website that never quite made it to publish. Her business was ready for its next chapter. She needed a website that could keep up.\n\nWe brought her signature Lead Like an Athlete approach to life with a clean, bold design built to resonate with executive women pursuing success without burnout. Clear messaging showcased her expertise, interactive scorecards and assessments invited visitors to engage, and a straightforward path to book a call gave referrals somewhere to take the next step.\n\nThree days later, FlexWithFE had a professional online home ready to share, support her upcoming book, and grow with her business.",
    favorite: "The interactive pop-up features like the scorecard and assessment on the home page and throughout the site.",
    impact: "Having a professionally designed website will drive organic branding and engagement for my business. It will help drive awareness much faster.",
    partnership: "Yes the new website incorporates all of my design goals with the ability to enhance and make updates as my business grows. I love the ownership and the consulting approach. 3 Day Launch drove the partnership vs asking me to provide her with everything and simply executing what I provided.",
    mockup: flexwithfeMockupMain.url,
    mockupDetails: [
      { label: "Homepage detail", src: flexwithfeMockupHome },
      { label: "Mobile & desktop view", src: flexwithfeMockupMain.url },
      { label: "Interior pages", src: flexwithfeMockupPages },
    ],
  },
  {
    name: "Alicia Kaestner",
    cardHeading: "Decades of expertise. A website ready for her next chapter.",
    popupHeading: "Decades of expertise. A website ready for her next chapter.",
    url: "https://thehighermark.com/",
    website: "The Higher Mark · Marketing & Consulting",
    mockup: highermarkMockupSecond.url,
    quote: "The overall look and feel is so clean and professional while also creating a sense of calm, clarity, and confidence—the exact feeling I was going for. Having a professional website gives potential customers a place to learn about me and will be a key part of growing my business. We absolutely accomplished my goals. I loved working with Sierra because she is so kind, warm, and welcoming, and I knew I was in good hands throughout the entire process.",
    story: "The overall look and feel is so clean and professional while also creating a sense of calm, clarity, and confidence—the exact feeling I was going for. Having a professional website gives potential customers a place to learn about me and will be a key part of growing my business. We absolutely accomplished my goals. I loved working with Sierra because she is so kind, warm, and welcoming, and I knew I was in good hands throughout the entire process.",
    caseStudy: "Alicia Kaestner brought decades of marketing and consulting experience to The Higher Mark. As she shaped the next season of her business, she needed a website that communicated that depth and spoke directly to the high-value clients she wanted to serve.\n\nWe translated her unique approach into a clean, graceful design with a natural feel and a confident presence. Thoughtfully organized messaging helped potential clients understand her expertise, explore her approach, and see the value of working together.\n\nThe design process also helped Alicia sharpen her business direction. In three days, she had a professional online home built around who she is and where she wanted to go.\n\n",
    favorite: "The overall look and feel is so clean and professional while also exuding a sense of calm, clarity, and confidence - the exact vibe that I was going for!",
    impact: "Having a professional website will be a key vehicle for potential customers to learn about me, which will ultimately impact me getting any business at all.",
    partnership: "Yes, absolutely we successfully accomplished my goals. I loved working with Sierra because she is so kind, so warm and welcoming and I knew I was in good hands throughout the whole process.",
    twoColumnMockups: true,
    mockupDetails: [
      { label: "Website overview", src: highermarkMockupMain.url },
      { label: "Tablet & phone view", src: highermarkMockupThird.url },
      { label: "Laptop mockup", src: highermarkMockupFourth.url },
    ],
  },
  {
    name: "Monika Lund",
    cardHeading: "Monika Lund, North Star Financial Solutions",
    popupHeading: "Making the next step feel simple, even when money feels complicated.",
    url: "https://northstarfinancialsolutionsllc.com/",
    website: "North Star Financial Solutions",
    quote: "Imagine a fairy website designer who could bring your vision to life in three days. Just tell her what you wish for, and she will get it done. It is now a clean, simple funnel for anyone ready to take the next step in their business or personal finances. My vision finally has momentum because my ideas are no longer trapped in my head. All of my expectations were exceeded, and Sierra brought my entire vision to life without me having to deal with any of the technical pieces.",
    story: "Imagine a fairy website designer who could bring your vision to life in three days. Just tell her what you wish for, and she will get it done. I loved all of it, but if I had to choose a favorite part, it would be the ability to continuously edit my website as I evolve. It is now a clean, simple funnel for anyone ready to take the next step in their business or personal finances. My vision finally has momentum because my ideas are no longer trapped in my head. All of my expectations were exceeded, and Sierra brought my entire vision to life without me having to deal with any of the technical pieces.",
     caseStudy: "\n\nMonika Lund helps people make sense of her finances. But her outdated, overly complicated website was adding to the overwhelm. It didn't reflect the professionalism of her business or give the people she met through networking and referrals a clear place to start.\n\nWe created a clean, welcoming website that made her services easy to understand, from bookkeeping and accounting to financial advising and budget planning. Clear offers and intuitive next steps help visitors find the right support and reach out with confidence.\n\nIn just three days, Monika had an online home that matched her vision, with the flexibility to keep updating it as her business evolves.",
    favorite: "Yes. No, seriously, all of it. But if I had to pick a favorite, it's my ability to continuously edit as I evolve.",
    impact: "My website is a clean, simple funnel for anyone who is ready to take the next step in their business or personal finances. My vision finally has momentum because my ideas are no longer trapped in my head.",
    partnership: "Yes - and all expectations were exceeded! My favorite part, aside from bringing my entire vision to life, was completely removing my having to deal with all the technical pieces.",
    mockup: northstarMockupMain.url,
    fullWidthMockups: true,
    mockupDetails: [
      { label: "Homepage detail", src: northstarMockupHome },
    ],
  },
  {
    name: "Laurie Nelson",
    cardHeading: "From brand-new business to first leads.",
    popupHeading: "From brand-new business to first leads.",
    url: "https://chicagoseniorrides.com/",
    website: "Chicago Senior Rides · Senior Transportation Services",
    quote: "I love the sense of flow my website has for the user, I feel like my website tells a story.",
    story: "I love the sense of flow my website has for the user. It tells the bigger story of my business and how it uniquely meets people's needs, along with smaller stories that showcase the services I offer. These stories invite people to learn more, while the personal touch helps build trust and lessen any hesitation about using my services. The Three Day Launch process helped me quickly bring my ideas into the world, and I love that my blog lives directly on my website instead of a separate platform. We absolutely met my design goals. Sierra was fast, responsive, and quickly understood my business. She asked thoughtful questions that even sparked new ideas, and it truly felt like we were a team working toward the same goals.",
    favorite: "I love the sense of flow my website has for the user, I feel like my website tells a story. The Three Day Launch design process helped me quickly get my ideas out to the world. I also like the addition of a blog post that appears on my website and not on some separate platform.",
    impact: "My website tells a bigger arc story (it explains my business and how it uniquely meets needs), and my website tells smaller stories (examples of services I offer). These stories entice people to learn more about me and my business. The personal touch that my website communicates will help people trust me and lessen hesitations to use my services.",
    partnership: "Yes, we definitely met my design goals! My favorite part of working together was the speed and responsiveness of my designer. My designer quickly understood my business goals and asked me good questions that sparked more business ideas. I feel like my designer and I are on a team with the same goals.",
     caseStudy: "\n\nLaurie Nelson was launching Chicago Senior Rides from scratch. She needed a website that introduced her services, helped local clients find her, and made reaching out feel comfortable for older adults looking for transportation support.\n\nWe helped organize her ideas into a clear story: who she helps, what makes her service personal, and how to get started. A warm, engaging design brought her personality forward, while easy navigation and a mobile-friendly layout kept her older audience at the center of every decision. Local SEO gave the new business a foundation for getting found.\n\nSoon after launch, her first website leads began coming in. Laurie had a welcoming place to introduce her business and start building relationships.\n\n",
    mockup: chicagoseniorridesMockup1.url,
    mockupDetails: [
      { label: "Walking destinations page", src: chicagoseniorridesMockup2.url },
      { label: "Door-to-door rides page", src: chicagoseniorridesMockup3.url },
    ],
  },
  {
    name: "Erika Hebert",
    cardHeading: "Serious expertise. Unmistakably her.",
    popupHeading: "Serious expertise. Unmistakably Erika.",
    url: "https://mavenyx.net/",
    website: "Mavenyx · Leadership Development & Coaching",
    quote: "Working with Three Day Launch was one of the best decisions I’ve made for my business. If you’re on the fence, let me tell you: do it! The process is fast, thoughtful, and surprisingly personal. Sierra doesn’t just ‘build a website,’ she learns you. She listens for the nuances, the energy, the intention behind your work, and then translates all of that into a brand presence that feels unmistakably you.",
    story: "Working with Three Day Launch was one of the best decisions I’ve made for my business. If you’re on the fence, let me tell you: do it! The process is fast, thoughtful, and surprisingly personal. Sierra doesn’t just ‘build a website,’ she learns you. She listens for the nuances, the energy, the intention behind your work, and then translates all of that into a brand presence that feels unmistakably you.\n\n\nMy favorite part of the experience was how seen and understood I felt throughout the entire process. Every design choice came with a clear explanation, every SEO decision was broken down in a way that made sense, and every step felt like true collaboration. The videos she created to walk me through options were incredibly helpful. It felt like having a creative partner right beside me.\n\n\nThe final product? A website that captures my essence with clarity, excitement, and authenticity. It’s beautiful, it’s strategic, and it’s something I’m genuinely proud to share. This update will absolutely elevate my business. It gives potential and current clients a clear, engaging way to understand what I offer, and it positions my brand with the professionalism and personality it deserves. If you want a website that reflects who you are, supports your business goals, and makes you excited to hit ‘publish,’ Three Day Launch is the way to go. It’s not just a service, it’s a partnership that delivers.",
    caseStudy: "Erika Hebert helps organizations prepare stronger leaders and build more effective teams. Her website needed to make that work easy to understand while capturing the personality and intention behind her brand.\n\nWe paired confident messaging with a design that felt personal to Erika. Clearly organized programs help different audiences find relevant support, while free leadership resources invite visitors to connect before booking a discovery call.\n\nThe result was a website that showcased her expertise, supported her lead-generation goals, and made her excited to share her business with the world.",
    favorite: "My favorite part of the experience was how seen and understood I felt throughout the entire process. Every design choice came with a clear explanation, every SEO decision was broken down in a way that made sense, and every step felt like true collaboration.",
    impact: "This update will absolutely elevate my business. It gives potential and current clients a clear, engaging way to understand what I offer, and it positions my brand with the professionalism and personality it deserves.",
    partnership: "Sierra doesn’t just ‘build a website,’ she learns you. She listens for the nuances, the energy, the intention behind your work, and then translates all of that into a brand presence that feels unmistakably you. It’s not just a service, it’s a partnership that delivers.",
    mockup: mavenyxMockupLaptop.url,
    mockupDetails: [
      { label: "Full page view", src: mavenyxMockupFull.url },
      { label: "Desktop monitor view", src: mavenyxMockupMonitor.url },
    ],
  },
  {
    name: "Janice Lodato",
    cardHeading: "A connection that starts before the first conversation.",
    popupHeading: "A connection that starts before the first conversation.",
    url: "https://janicelodato.com/",
    website: "The Grief Gardener · Grief Coaching & Support",
    mockup: griefgardenerMockupMain.url,
    quote: "Working with Sierra is a dream come true! Sierra's designs are so beautiful and they are SMART. She blends her artistry with SEO knowledge, technical expertise, and just the right amount and type of content for your audience. Sierra is super responsive to questions and will make sure you're satisfied with every step of the process and the finished product. I highly recommend working with Sierra!",
    story: "Working with Sierra is a dream come true! Sierra's designs are so beautiful and they are SMART. She blends her artistry with SEO knowledge, technical expertise, and just the right amount and type of content for your audience. Sierra is super responsive to questions and will make sure you're satisfied with every step of the process and the finished product. I highly recommend working with Sierra!",
    caseStudy: "For Janice Lodato, a website needed to help people feel seen, comfortable, and ready to connect. Every part of the experience had to support that personal welcome.\n\nWe paired a calming visual design with thoughtfully selected content, bringing together artistry, SEO knowledge, and technical expertise. The goal was to give visitors enough information to recognize themselves in her work and feel encouraged to learn more.\n\nThat feeling came through. After launch, Janice shared that visitors kept telling her how calm and connected they felt on the site. Her new online home was already making the kind of first impression she wanted.\n\n",
    favorite: "My new website is so beautiful. Visitors to my new website keep telling me how they feel so calm and connected when they're on the site.",
    impact: "People will feel seen and want to connect and learn more.",
    partnership: "I loved how responsive you were throughout the process. I also really appreciate your expertise and how you thoroughly answered all my questions.",
    mockupDetails: [
      { label: "Full page view", src: griefgardenerMockupSecond.url },
      { label: "Desktop & mobile view", src: griefgardenerMockupDevices.url },
    ],
  },
  {
    name: "Neoorah Williams",
    cardHeading: "Her clients knew her value. Time for new people to discover it.",
    popupHeading: "Her clients knew her value. Time for new people to discover it.",
    url: "https://lovingmefirstco.com/",
    website: "Loving Me First Co.\u00a0",
    quote: "The welcome page is warm, vibrant and welcoming. Everything I needed and much more!",
    story: "If you need a professional and client centered website home, look no further! Three Day Launch Is fast, very professional, organized, and made everything easy to follow and understand. My new website makes me so happy. Sierra over exceeded every expectation I could have had, and brought everything I imagined to life. Book now, you will not regret it!",
    favorite: "The welcome page is warm, vibrant and welcoming. Everything I needed and much more!",
    impact: "The creation of this beautiful website will help me a great deal with connecting with my audience and inviting people who dont know of my services yet.",
    partnership: "Sierra was such a beautiful spirit to work with. I loved every part of this journey, knowing that my website launch was in good, compassionate and professional hands. You over exceeded my expectations and I am forever grateful. I could not have pulled this off without Three Day Launch.",
    caseStudy: "\nNeoorah’s coaching business was growing, and her clients were getting great results. But with no website to send potential clients to, making new connections meant explaining everything from scratch.\n\nWe created a warm, vibrant online home that introduced her work and made her coaching packages easy to explore. Her lead magnet quiz gave curious visitors an inviting first step, while clearly organized programs helped those ready for deeper support find their way to working with her.\n\nThen we added a little magic: animated 3D butterflies that brought movement and personality to a polished, professional design. Neoorah now had a website that captured her spirit and gave her business a clear place to welcome new leads.",
    mockup: lovingmefirstMockupDevices.url,
    mockupDetails: [
      { label: "Laptop view", src: lovingmefirstMockupLaptop.url },
      { label: "Full page view", src: lovingmefirstMockupFull.url },
    ],
  },
] as const;

type RecentLaunch = {
  name: string;
  cardHeading?: string;
  popupHeading?: string;
  url: string;
  website: string;
  quote: string;
  story: string;
  caseStudy?: string;
  favorite: string;
  impact: string;
  partnership: string;
  mockup?: string;
  fullWidthMockups?: boolean;
  twoColumnMockups?: boolean;
  mockupDetails?: readonly { readonly label: string; readonly src: string }[];
};

function WebsiteMockupPlaceholder({ project, compact = false, onClick }: { project: RecentLaunch; compact?: boolean; onClick?: () => void }) {
  const mockup = "mockup" in project && project.mockup ? (
    <div className="overflow-hidden rounded-lg border border-secondary-foreground/15 bg-secondary">
      <img
        src={project.mockup}
        alt={`${project.website} website mockup shown on a laptop and phone`}
        className={`w-full object-cover ${compact ? "aspect-[16/10]" : "aspect-[16/10]"}`}
        loading="lazy"
      />
    </div>
  ) : (
    <div className="overflow-hidden rounded-lg border border-secondary-foreground/15 bg-secondary" aria-label={`Mockup placeholder for ${project.name}'s website`}>
      <div className="flex h-8 items-center gap-1.5 border-b border-secondary-foreground/15 px-3" aria-hidden="true">
        <span className="h-2 w-2 rounded-full bg-primary" />
        <span className="h-2 w-2 rounded-full bg-secondary-foreground/35" />
        <span className="h-2 w-2 rounded-full bg-secondary-foreground/20" />
        <span className="ml-2 h-2 flex-1 rounded-full bg-secondary-foreground/10" />
      </div>
      <div className={`flex aspect-[16/10] flex-col items-center justify-center px-5 text-center ${compact ? "gap-2" : "gap-4"}`}>
        <Image className={`${compact ? "h-8 w-8" : "h-12 w-12"} text-primary`} aria-hidden="true" />
        <div>
          <p className={`${compact ? "text-base" : "text-xl"} font-bold text-secondary-foreground`}>{project.website}</p>
          <p className="mt-1 text-xs font-semibold uppercase text-secondary-foreground/55">Website mockup coming soon</p>
        </div>
      </div>
    </div>
  );

  if (!onClick) return mockup;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`See more about ${project.website}`}
      className="block w-full cursor-pointer text-left transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {mockup}
    </button>
  );
}

function LaunchStoryDialog({ project, open, onOpenChange, useLeadForm }: { project: RecentLaunch; open: boolean; onOpenChange: (open: boolean) => void; useLeadForm?: boolean }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="launch-story-dialog max-h-[92vh] w-[calc(100%-1.5rem)] max-w-5xl overflow-y-auto p-0">
        <DialogClose className="absolute right-3 top-3 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <div className="bg-secondary p-5 sm:p-8">
          <WebsiteMockupPlaceholder project={project} />
        </div>
        <div className="p-6 sm:p-10">
          <DialogHeader>
            <DialogTitle className="text-3xl leading-tight sm:text-5xl">{project.popupHeading ?? project.name}</DialogTitle>
            <DialogDescription className="text-base">{project.website}</DialogDescription>
          </DialogHeader>

          <blockquote className="mt-8 border-l-4 border-primary pl-5">
            <div className="space-y-4 text-lg font-medium leading-relaxed text-muted-foreground sm:text-xl">
              {project.story.split("\n\n").map((paragraph, i, arr) => (
                <p key={paragraph.slice(0, 40)}>
                  {i === 0 ? "“" : ""}
                  {paragraph}
                  {i === arr.length - 1 ? "”" : ""}
                </p>
              ))}
            </div>
            <footer className="mt-3 text-sm font-bold text-muted-foreground">{project.name}</footer>
          </blockquote>

          {"caseStudy" in project && project.caseStudy && (
            <div className="mt-8 space-y-4 text-base leading-relaxed text-muted-foreground">
              {project.caseStudy.split("\n\n").map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>
          )}


          <div className="mt-10 grid grid-cols-1 gap-3" aria-label={`${project.name} supporting mockups`}>
            {("mockupDetails" in project && project.mockupDetails
              ? project.mockupDetails
              : [
                  { label: "Homepage detail", src: null },
                  { label: "Mobile view", src: null },
                  { label: "Interior page", src: null },
                ]
            ).map((item) =>
              item.src ? (
                <div key={item.label} className="overflow-hidden border border-border bg-muted">
                  <img src={item.src} alt={`${project.website} ${item.label.toLowerCase()}`} className="h-auto w-full object-cover object-top" loading="lazy" />
                </div>
              ) : (
                <div key={item.label} className="flex aspect-[16/10] items-center justify-center border border-border bg-muted px-4 text-center">
                  <div>
                    <Image className="mx-auto h-6 w-6 text-primary" aria-hidden="true" />
                    <p className="mt-2 text-xs font-semibold text-muted-foreground">{item.label} coming soon</p>
                  </div>
                </div>
              ),
            )}
          </div>

          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <BookButton size="lg" useLeadForm={useLeadForm} className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
              Book a Free Call <ArrowRight className="ml-2 h-4 w-4" />
            </BookButton>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              Explore More Designs
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompactLaunchCard({
  project,
  onSeeMore,
  className = "",
}: {
  project: RecentLaunch;
  onSeeMore: (p: RecentLaunch) => void;
  className?: string;
}) {
  return (
    <article className={`group flex min-w-0 flex-col overflow-hidden rounded-lg border border-secondary-foreground/15 bg-secondary-foreground/5 ${className}`}>
      <WebsiteMockupPlaceholder project={project} compact onClick={() => onSeeMore(project)} />
      <div className="flex flex-1 flex-col p-6">
        <h3 className="text-xl font-bold">{project.cardHeading ?? project.name}</h3>
        <Button variant="link" onClick={() => onSeeMore(project)} className="mt-auto h-auto justify-start px-0 pt-6 text-primary">
          See More <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </article>
  );
}


function RecentWebsiteLaunches({
  useLeadForm,
  overrides,
  embedded = false,
}: {
  useLeadForm?: boolean;
  overrides?: Record<string, Partial<RecentLaunch>>;
  embedded?: boolean;
} = {}) {
  const [selectedProject, setSelectedProject] = useState<RecentLaunch | null>(null);
  const launches = recentLaunches.map((project) => {
    const override = overrides?.[project.name];
    return override ? { ...project, ...override } : project;
  });

  const content = (
    <>
      {/* Mobile: swipe through all 7 launches */}
      <div className="-mx-6 mt-14 flex snap-x snap-mandatory gap-7 overflow-x-auto px-6 pb-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:hidden">
        {launches.map((project) => (
          <CompactLaunchCard key={project.name} project={project} onSeeMore={setSelectedProject} className="w-[85%] shrink-0 snap-center" />
        ))}
      </div>

      {/* Desktop: all launches in one continuously looping row */}
      <div
        className="group hidden overflow-hidden md:mt-14 md:block"
        style={{
          maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
        }}
      >
        <div className="marquee flex w-max gap-7 pr-7 group-hover:[animation-play-state:paused]" style={{ animationDuration: "75s" }}>
          {[...launches, ...launches].map((project, index) => (
            <CompactLaunchCard
              key={`${project.name}-${index}`}
              project={project}
              onSeeMore={setSelectedProject}
              className="w-[320px] shrink-0"
            />
          ))}
        </div>
      </div>
    </>
  );

  const dialog = selectedProject ? (
    <LaunchStoryDialog
      project={selectedProject}
      open
      useLeadForm={useLeadForm}
      onOpenChange={(open) => {
        if (!open) setSelectedProject(null);
      }}
    />
  ) : null;

  if (embedded) {
    return (
      <div id="recent-launches" className="mt-20 scroll-mt-24">
        {content}
        {dialog}
      </div>
    );
  }

  return (
    <section
      id="recent-launches"
      className="relative bg-secondary bg-cover bg-center bg-scroll py-20 text-secondary-foreground md:bg-fixed md:py-28"
      style={{ backgroundImage: `url(${recentLaunchesBg.url})` }}
      aria-labelledby="recent-launches-heading"
    >
      <div className="absolute inset-0 bg-secondary/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6">{content}</div>
      {dialog}
    </section>
  );
}

export default RecentWebsiteLaunches;
export { RecentWebsiteLaunches };
